import { useContext, useMemo } from 'react';
import { Button, DialogActions } from '@mui/material';
import clsx from 'clsx';
import { isUndefined } from 'lodash';
import { DELETE_COMPANY_ENABLER } from 'common/constants/features';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import FeatureEnabler from 'components/FeatureEnabler';
import DialogContext from 'context/DialogContext';
import { Logger } from 'utilities';
import { FormDialogProps } from './FormDialog.props';
import { formDialogClasses, StyledDialog } from './FormDialog.styles';
import ConfirmationCancelDialog from '../ConfirmationCancelDialog/ConfirmationCancelDialog';
import DeleteButton from '../DeleteButton/DeleteButton';

const FormDialog = ({
  title = '',
  hideTitle,
  open,
  editMode,
  isValid,
  children,
  onSave,
  onDelete,
  deleteText,
  onClose,
  confirmCancel = false,
  subtitle,
  bigDialog = false,
  PaperProps,
  isLoading = false,
  loadingText,
  customButtonLabel,
  shouldCloseOnDelete = false,
  allowDelete,
  toolTipText,
  turnOnDeleteFunctionality,
  customActionButtonLabel,
  onCustomAction,
  showCancelButton = true,
  disableTitleClose = true,
  showSaveButton = true,
  customCancelButton,
  customButton,
  classesContent,
  shouldCheckFeatureEnabler = false,
  doPostSaveClose = true,
  cellKey,
  disableWhenLedgerOpen,
}: FormDialogProps) => {
  const [{ isShowLoadingProgress = false }] = useStore() as unknown as UseStoreValues;
  const { showDialog } = useContext(DialogContext);

  /**
   * handleSave now uses try/catch to ensure that if the API call fails,
   * the dialog remains open so the user knows that something went wrong.
   */
  const handleSave = async () => {
    if (onSave) {
      try {
        const result = await onSave();
        if (doPostSaveClose) {
          onClose(result);
        }
      } catch (error) {
        // OWASP A10: Mishandling of Exceptional Conditions - log the error and keep the UI stable
        Logger.error('Form submission failed:', error);
      }
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      try {
        const aux = await onDelete();
        if (shouldCloseOnDelete) {
          onClose(aux);
        }
      } catch (error) {
        Logger.error('Delete operation failed:', error);
      }
    }
  };

  const handleClose = () => {
    if (confirmCancel && !editMode) {
      showDialog({
        wrapper: ConfirmationCancelDialog,
        actions: [
          {
            label: 'Cancel Anyways',
            variant: 'contained',
            type: 'danger',
            callback: onClose,
          },
        ],
      });
    } else {
      onClose();
    }
  };

  const getActionButtonText = () => (editMode ? 'Update' : 'Save');

  const showCustomActionButton = customActionButtonLabel && onCustomAction;
  let showDeleteButton = !isUndefined(onDelete) && (editMode || allowDelete);

  if (turnOnDeleteFunctionality === false) {
    showDeleteButton = false;
  }

  const enableDeleteButton = useMemo(
    () => shouldCheckFeatureEnabler || allowDelete,
    [shouldCheckFeatureEnabler, allowDelete]
  );

  const displayDeleteBtn = () => {
    if (shouldCheckFeatureEnabler) {
      return (
        <FeatureEnabler enabler={DELETE_COMPANY_ENABLER} hideComponent>
          <DeleteButton
            toolTipText={toolTipText}
            allowDelete={allowDelete}
            enableDeleteButton={enableDeleteButton}
            deleteText={deleteText}
            handleDelete={handleDelete}
          />
        </FeatureEnabler>
      );
    }
    return (
      <DeleteButton
        toolTipText={toolTipText}
        allowDelete={allowDelete}
        enableDeleteButton={enableDeleteButton}
        deleteText={deleteText}
        handleDelete={handleDelete}
      />
    );
  };

  return (
    <StyledDialog
      title={title}
      hideTitle={hideTitle}
      subtitle={subtitle}
      maxWidth={bigDialog ? 'md' : 'sm'}
      fullWidth
      onClose={handleClose}
      open={open}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        disableTitleClose,
        ...(cellKey !== undefined && { cellKey }),
        ...(disableWhenLedgerOpen !== undefined && { disableWhenLedgerOpen }),
      }}
      PaperProps={PaperProps}
      isLoading={isLoading || isShowLoadingProgress}
      loadingText={loadingText}>
      <div style={classesContent || {}} className={classesContent ? undefined : formDialogClasses.content}>
        {children}
      </div>
      <DialogActions className={formDialogClasses.actions}>
        {showDeleteButton && displayDeleteBtn()}
        {showCustomActionButton && (
          <Button
            id="custom-btn"
            className={clsx(formDialogClasses.actionButton, formDialogClasses.autoMarginRight)}
            color="primary"
            variant="text"
            onClick={onCustomAction}>
            {customActionButtonLabel}
          </Button>
        )}
        {showCancelButton && !customCancelButton && (
          <Button
            id="company-cancel-btn"
            className={formDialogClasses.actionButton}
            color="primary"
            onClick={handleClose}
            // Only disabled during local loading, NOT global progress.
            // This allows users to cancel out of invalid form states.
            disabled={isLoading}>
            Cancel
          </Button>
        )}
        {customCancelButton}
        {showSaveButton && (
          <Button
            id="company-save-btn"
            className={formDialogClasses.actionButton}
            variant="contained"
            color="secondary"
            onClick={handleSave}
            disabled={!isValid || isShowLoadingProgress || isLoading}>
            {customButtonLabel ?? getActionButtonText()}
          </Button>
        )}
        {customButton}
      </DialogActions>
    </StyledDialog>
  );
};

export default FormDialog;
